import { Box } from '@mui/material';
import { Editor } from '../../../../../components/wysiwyg/Editor';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from '../../../../../store/snackbar';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useDebounceEffect } from '../../../../../utils/useDebounceEffect';
import { z } from 'zod';

const activityDescriptionQuery = graphql(`
	query journeyDetail_getActivityDescription($activityId: UUID!) {
		getActivity(by: { id: $activityId }) {
			description
		}
	}
`);

const updateDescriptionMutation = graphql(`
	mutation journeyDetail_updateDescription($id: UUID!, $description: String) {
		updateActivity(by: { id: $id }, data: { description: $description }) {
			ok
			errorMessage
		}
	}
`);

export const descriptionEditDrawerSchema = z.object({
	activityId: z.string(),
});

export const DescriptionEditDrawer: FC<z.infer<typeof descriptionEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const [description, setDescription] = useState('');
	const [isEditorReady, setIsEditorReady] = useState(false);
	const { openSnackbar } = useSnackbar();

	const { data, isPending } = useApiQuery(activityDescriptionQuery, {
		variables: { activityId },
	});

	const { mutate: updateActivityDescription } = useApiMutation(updateDescriptionMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: { error: intl.formatMessage({ id: 'widget.description.update.error' }) },
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			}),
	});

	useEffect(() => {
		if (!isPending) {
			setDescription(data?.getActivity?.description ?? '');
			setIsEditorReady(true);
		}
		// excluding other dependencies as latest data are passed in when isPending changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPending]);

	useDebounceEffect(() => {
		if (isEditorReady && data?.getActivity?.description !== description) {
			updateActivityDescription({ id: activityId, description });
		}
	}, [description, data, isEditorReady]);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.description.title' })} />
			<Box width={'100%'}>{isEditorReady && <Editor placeholder={'...'} state={description} setState={setDescription} />}</Box>
		</DrawerWrapper>
	);
};
